import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import LoadFailureNote from "../components/LoadFailureNote";
import { ERROR_CODE_LABELS, errorCodeLabel, errorTimeLabel, fetchAdminErrors, sendTestAlert } from "../lib/adminErrors";

const control = {
  height: 36,
  padding: "0 10px",
  border: "1px solid var(--nw-line-strong)",
  borderRadius: "var(--nw-radius-control)",
  background: "var(--nw-surface)",
  color: "var(--nw-ink)",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  fontSize: 13,
};

/**
 * `/admin/errors` — the Errors section of the one admin area (issue #1191,
 * D234). A read-only table of the last 14 days of failed requests. A row is
 * a time, a method, a route TEMPLATE, a status, a code and a request id: the
 * same fields the API stores and nothing else, so there is nothing personal
 * to show. The request id is what a person can quote from the
 * `X-Request-Id` header of the failed response.
 *
 * Laptop-first (D228.2): a wide table in a card, scrolling sideways on a
 * narrow screen rather than reflowing. Admin-only in the router.
 */
export default function AdminErrors() {
  const [events, setEvents] = useState([]);
  const [codes, setCodes] = useState(Object.keys(ERROR_CODE_LABELS));
  const [retentionDays, setRetentionDays] = useState(14);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [route, setRoute] = useState("");
  const [status, setStatus] = useState("");
  const [code, setCode] = useState("");
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertResult, setAlertResult] = useState(null);

  // Issue #1341: the live smoke for the 5xx alert. The route fails on
  // purpose; a 500 is the expected answer.
  async function onTestAlert() {
    setAlertBusy(true);
    setAlertResult(null);
    try {
      const { status, requestId } = await sendTestAlert();
      setAlertResult(status === 500 ? { sent: true, requestId } : { sent: false, status });
    } catch {
      setAlertResult({ sent: false, status: null });
    } finally {
      setAlertBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    // Typing in a filter waits a moment before asking, so one query goes out.
    const timer = setTimeout(() => {
      fetchAdminErrors({ route, status, code })
        .then((data) => {
          if (cancelled) return;
          setEvents(Array.isArray(data.events) ? data.events : []);
          if (Array.isArray(data.codes)) setCodes(data.codes);
          if (data.retention_days) setRetentionDays(data.retention_days);
          setLoadFailed(false);
          setLoaded(true);
        })
        .catch(() => {
          if (!cancelled) {
            setLoadFailed(true);
            setLoaded(true);
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [route, status, code, reloadKey]);

  const filtered = route.trim() || status.trim() || code;

  return (
    <div className="nw-page-stack" data-testid="admin-errors">
      <div>
        <Link to="/admin" className="nw-caption inline-flex items-center gap-1" style={{ color: "var(--nw-ink-muted)" }}>
          <ChevronLeft size={14} /> Admin
        </Link>
        <p className="nw-eyebrow" style={{ marginTop: "var(--nw-space-tight)" }}>Admin</p>
        <h1 className="nw-title" style={{ marginTop: "var(--nw-space-xtight)" }}>Errors</h1>
        <p className="nw-lede" style={{ marginTop: "var(--nw-space-tight)", maxWidth: 640 }}>
          Failed requests from the last {retentionDays} days. Each one is a route, a status, a code and a request id, with
          no names, emails or request contents.
        </p>
      </div>

      <div className="flex flex-wrap items-center" style={{ gap: "var(--nw-space-inline)" }}>
        <button
          type="button"
          style={{ ...control, height: "auto", minHeight: 44, padding: "0 16px", cursor: alertBusy ? "default" : "pointer", fontWeight: 600 }}
          disabled={alertBusy}
          onClick={onTestAlert}
          data-testid="admin-errors-test-alert"
        >
          Send a test alert
        </button>
        {alertResult && (
          <p
            className="nw-help"
            role="status"
            data-testid={alertResult.sent ? "admin-errors-test-alert-sent" : "admin-errors-test-alert-failed"}
          >
            {alertResult.sent
              ? `Test error sent${alertResult.requestId ? ` (request ${alertResult.requestId})` : ""}. Check the alert channel.`
              : alertResult.status === null
                ? "The test did not run (no response)."
                : `The test did not run (status ${alertResult.status}).`}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-end" style={{ gap: "var(--nw-space-inline)" }}>
        <label className="flex flex-col" style={{ gap: "var(--nw-space-xtight)" }}>
          <span className="nw-label">Route</span>
          <input style={{ ...control, width: 260 }} value={route} onChange={(e) => setRoute(e.target.value)} placeholder="/api/auth/register" data-testid="errors-filter-route" />
        </label>
        <label className="flex flex-col" style={{ gap: "var(--nw-space-xtight)" }}>
          <span className="nw-label">Status</span>
          <input style={{ ...control, width: 90 }} inputMode="numeric" maxLength={3} value={status} onChange={(e) => setStatus(e.target.value)} placeholder="400" data-testid="errors-filter-status" />
        </label>
        <label className="flex flex-col" style={{ gap: "var(--nw-space-xtight)" }}>
          <span className="nw-label">Code</span>
          <select style={{ ...control, width: 240 }} value={code} onChange={(e) => setCode(e.target.value)} data-testid="errors-filter-code">
            <option value="">Any</option>
            {codes.map((c) => (
              <option key={c} value={c}>{errorCodeLabel(c)}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ border: "var(--nw-border-card)", borderRadius: "var(--nw-radius-card)", background: "var(--nw-surface)" }}>
        {loadFailed ? (
          <LoadFailureNote what="the error record" onRetry={() => setReloadKey((k) => k + 1)} testId="admin-errors-failed" />
        ) : !loaded ? (
          <p className="nw-help" style={{ padding: "var(--nw-space-card)" }} data-testid="admin-errors-loading">Loading…</p>
        ) : events.length === 0 ? (
          <p className="nw-body" style={{ padding: "var(--nw-space-card-hero)" }} data-testid="admin-errors-empty">
            {filtered ? "No failed requests match these filters." : `No failed requests in the last ${retentionDays} days.`}
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="w-full" style={{ borderCollapse: "collapse", minWidth: 760 }} data-testid="admin-errors-table">
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid var(--nw-line)" }}>
                  {["When", "Method", "Route", "Status", "Code", "Request id"].map((h) => (
                    <th key={h} className="nw-label" style={{ padding: "10px 12px", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} style={{ borderBottom: "1px solid var(--nw-line-faint)" }} data-testid="admin-errors-row">
                    <td className="nw-prose" style={{ padding: "8px 12px", color: "var(--nw-ink)", whiteSpace: "nowrap" }}>{errorTimeLabel(e.at)}</td>
                    <td className="nw-caption-strong" style={{ padding: "8px 12px", color: "var(--nw-ink)" }}>{e.method}</td>
                    <td className="nw-prose" style={{ padding: "8px 12px", color: "var(--nw-ink)", fontFamily: "ui-monospace, Menlo, monospace" }}>{e.route_template}</td>
                    <td className="nw-caption-strong" style={{ padding: "8px 12px", color: e.status >= 500 ? "var(--nw-alert)" : "var(--nw-ink)" }}>{e.status}</td>
                    <td className="nw-prose" style={{ padding: "8px 12px", color: "var(--nw-ink)" }}>{errorCodeLabel(e.code)}</td>
                    <td className="nw-caption" style={{ padding: "8px 12px", color: "var(--nw-ink-muted)", fontFamily: "ui-monospace, Menlo, monospace" }}>{e.request_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
